import React from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Plane, Briefcase, CheckCircle, Wallet, Users, BarChart3, Settings, LogOut, Shield, Building2, Server, Receipt, Bell, Play } from 'lucide-react';
interface SidebarProps {
  onLogout: () => void;
  userRole: string | null;
}
export function Sidebar({
  onLogout,
  userRole
}: SidebarProps) {
  // Define nav items based on role
  const getNavItems = () => {
    const commonItems = [{
      icon: Play,
      label: 'Demo Flow',
      path: '/demo'
    }, {
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/'
    }];
    switch (userRole) {
      case 'super_admin':
        return [{
          icon: Play,
          label: 'Demo Flow',
          path: '/demo'
        }, {
          icon: LayoutDashboard,
          label: 'Platform Overview',
          path: '/super-admin'
        }, {
          icon: Building2,
          label: 'Tenants',
          path: '/super-admin/tenants'
        }, {
          icon: Server,
          label: 'System Settings',
          path: '/super-admin/settings'
        }, {
          icon: BarChart3,
          label: 'Global Analytics',
          path: '/super-admin/analytics'
        }, {
          icon: Bell,
          label: 'Notifications',
          path: '/notifications'
        }];
      case 'tenant_admin':
        return [...commonItems, {
          icon: Plane,
          label: 'Book Travel',
          path: '/booking'
        }, {
          icon: Briefcase,
          label: 'My Trips',
          path: '/trips'
        }, {
          icon: CheckCircle,
          label: 'Approvals',
          path: '/approvals',
          badge: '3'
        }, {
          icon: Wallet,
          label: 'Budget',
          path: '/budget'
        }, {
          icon: Receipt,
          label: 'Expenses',
          path: '/expenses'
        }, {
          icon: Users,
          label: 'Team Members',
          path: '/admin/users'
        }, {
          icon: Users,
          label: 'Travelers',
          path: '/travelers'
        }, {
          icon: BarChart3,
          label: 'Reports',
          path: '/reports'
        }, {
          icon: Bell,
          label: 'Notifications',
          path: '/notifications'
        }, {
          icon: Settings,
          label: 'Settings',
          path: '/settings'
        }];
      case 'manager':
        return [...commonItems, {
          icon: Plane,
          label: 'Book Travel',
          path: '/booking'
        }, {
          icon: Briefcase,
          label: 'My Trips',
          path: '/trips'
        }, {
          icon: CheckCircle,
          label: 'Approvals',
          path: '/approvals',
          badge: '3'
        }, {
          icon: Receipt,
          label: 'Expenses',
          path: '/expenses'
        }, {
          icon: Users,
          label: 'My Team',
          path: '/travelers'
        }, {
          icon: BarChart3,
          label: 'Reports',
          path: '/reports'
        }, {
          icon: Bell,
          label: 'Notifications',
          path: '/notifications'
        }];
      case 'traveler':
      default:
        return [...commonItems, {
          icon: Plane,
          label: 'Book Travel',
          path: '/booking'
        }, {
          icon: Briefcase,
          label: 'My Trips',
          path: '/trips'
        }, {
          icon: Wallet,
          label: 'My Budget',
          path: '/budget'
        }, {
          icon: Bell,
          label: 'Notifications',
          path: '/notifications'
        }];
    }
  };
  const navItems = getNavItems();
  const getRoleLabel = () => {
    switch (userRole) {
      case 'super_admin':
        return {
          label: 'Super Admin',
          color: 'text-purple-400'
        };
      case 'tenant_admin':
        return {
          label: 'Admin',
          color: 'text-blue-400'
        };
      case 'manager':
        return {
          label: 'Manager',
          color: 'text-emerald-400'
        };
      default:
        return {
          label: 'Traveler',
          color: 'text-slate-400'
        };
    }
  };
  const roleInfo = getRoleLabel();
  return <aside className="fixed left-0 top-0 z-40 h-screen w-64 bg-slate-900 text-slate-400 transition-transform" data-id="element-101">
      <div className="flex h-16 items-center border-b border-slate-800 px-6" data-id="element-102">
        <Plane className="h-8 w-8 text-blue-500 mr-3" data-id="element-103" />
        <span className="text-xl font-bold text-white" data-id="element-104">TravelHub</span>
      </div>

      <div className="flex flex-col justify-between h-[calc(100vh-4rem)]" data-id="element-105">
        <nav className="space-y-1 px-3 py-4" data-id="element-106">
          {navItems.map(item => <NavLink key={item.label} to={item.path} className={({
          isActive
        }) => `flex items-center rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-blue-600/10 text-white' : 'hover:bg-slate-800 hover:text-white'}`} data-id="element-107">
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0" data-id="element-108" />
              <span className="flex-1" data-id="element-109">{item.label}</span>
              {item.badge && <span className="ml-auto inline-block rounded-full bg-blue-600 px-2 py-0.5 text-xs font-medium text-white" data-id="element-110">
                  {item.badge}
                </span>}
            </NavLink>)}
        </nav>

        <div className="border-t border-slate-800 p-4" data-id="element-111">
          <div className="flex items-center gap-3 mb-4" data-id="element-112">
            <img className="h-9 w-9 rounded-full bg-slate-700" src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80" alt="User avatar" data-id="element-113" />
            <div className="flex-1 min-w-0" data-id="element-114">
              <p className="truncate text-sm font-medium text-white" data-id="element-115">
                Alex Morgan
              </p>
              <p className={`truncate text-xs ${roleInfo.color}`} data-id="element-116">
                {roleInfo.label}
              </p>
            </div>
          </div>
          <button onClick={onLogout} className="flex w-full items-center rounded-lg px-3 py-2 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors" data-id="element-117">
            <LogOut className="mr-3 h-5 w-5" data-id="element-118" />
            Sign Out
          </button>
        </div>
      </div>
    </aside>;
}